// Shared helpers for image corner-radius clipping.
//
// SVG <rect> only supports a single rx/ry pair, so to allow each of the four
// corners to have its own radius we build a <path> that traces a rounded
// rectangle with per-corner arc segments. This module is used by both the live
// renderer (ElementRenderer.jsx) and the SVG export serializer (animation.js)
// so the on-canvas preview and the exported file stay identical.

// Resolve a single corner's radius (px or %) to absolute pixels.
//   value  - the corner's radius value (number)
//   unit   - "px" | "%"
//   w, h   - element size (used for % and for clamping)
//   edge   - the length of the edge this corner sits on (min(w,h) for % mode,
//            so % stays consistent with the "shorter edge" Figma semantics)
function resolveRadius(value, unit, w, h) {
  const v = Math.max(0, Number(value) || 0);
  if (unit === "%") {
    const factor = Math.max(0, Math.min(50, v)) / 100;
    const base = Math.min(w, h);
    return base * factor;
  }
  return v;
}

// Build an SVG path "d" string for a rounded rectangle with independent
// corner radii. Corners that have a 0 radius render as sharp right angles.
//
//   x, y        - top-left of the rect
//   w, h        - width / height
//   rTL..rBL    - absolute (px) radius for each corner
//
// The path starts at the top edge after the TL arc and goes clockwise.
// SVG arc flag convention: large-arc-flag=0 (we never exceed a quarter),
// sweep-flag=1 (clockwise).
export function roundedRectPath(x, y, w, h, rTL, rTR, rBR, rBL) {
  // Clamp adjacent corners so their combined radius never exceeds the edge
  // length (SVG would otherwise render undefined shapes). We scale both down
  // proportionally when they overflow, matching how browsers handle <rect rx>.
  const fit = (a, b, max) => {
    if (a + b <= max || a + b === 0) return [a, b];
    const k = max / (a + b);
    return [a * k, b * k];
  };
  [rTL, rTR] = fit(rTL, rTR, w);
  [rTR, rBR] = fit(rTR, rBR, h);
  [rBR, rBL] = fit(rBR, rBL, w);
  [rBL, rTL] = fit(rBL, rTL, h);

  const right = x + w;
  const bottom = y + h;

  // Move to the start of the top edge (after TL arc), then trace clockwise.
  const parts = [
    `M ${x + rTL} ${y}`,
    // Top edge -> TR corner
    `L ${right - rTR} ${y}`,
    rTR > 0 ? `A ${rTR} ${rTR} 0 0 1 ${right} ${y + rTR}` : `L ${right} ${y}`,
    // Right edge -> BR corner
    `L ${right} ${bottom - rBR}`,
    rBR > 0 ? `A ${rBR} ${rBR} 0 0 1 ${right - rBR} ${bottom}` : `L ${right} ${bottom}`,
    // Bottom edge -> BL corner
    `L ${x + rBL} ${bottom}`,
    rBL > 0 ? `A ${rBL} ${rBL} 0 0 1 ${x} ${bottom - rBL}` : `L ${x} ${bottom}`,
    // Left edge -> TL corner
    `L ${x} ${y + rTL}`,
    rTL > 0 ? `A ${rTL} ${rTL} 0 0 1 ${x + rTL} ${y}` : `L ${x} ${y}`,
    "Z",
  ];
  return parts.join(" ");
}

// Resolve the four corner radii (px) from an image element's attrs.
// Returns { rTL, rTR, rBR, rBL }.
//
// When `cornerIndividual` is true, each corner uses its own value
// (cornerTL/TR/BR/BL). Otherwise all four corners share `cornerRadius`.
export function resolveCornerRadii(attrs, w, h) {
  const unit = attrs.cornerRadiusUnit === "%" ? "%" : "px";
  if (attrs.cornerIndividual) {
    return {
      rTL: resolveRadius(attrs.cornerTL, unit, w, h),
      rTR: resolveRadius(attrs.cornerTR, unit, w, h),
      rBR: resolveRadius(attrs.cornerBR, unit, w, h),
      rBL: resolveRadius(attrs.cornerBL, unit, w, h),
    };
  }
  const r = resolveRadius(attrs.cornerRadius, unit, w, h);
  return { rTL: r, rTR: r, rBR: r, rBL: r };
}

// True if any corner has a non-zero radius (i.e. clipping is needed).
export function hasCornerRadius(attrs) {
  if (attrs.cornerIndividual) {
    return ["cornerTL", "cornerTR", "cornerBR", "cornerBL"].some(
      (k) => Number(attrs[k]) > 0
    );
  }
  return Number(attrs.cornerRadius) > 0;
}

// ---- Polygon corner rounding ----
//
// Convert a polygon's vertex list into a rounded-corner SVG path. Each vertex
// is replaced by a circular arc that is TANGENT to both incident edges, so the
// transition from each straight edge into the arc is perfectly smooth (no
// kinks, no "clipped" look).
//
// Geometry (the key to smoothness):
//   For a vertex with interior angle θ and desired fillet radius R, the arc
//   is tangent to each edge at distance  d = R / tan(θ/2)  from the vertex.
//   The arc itself has radius R. Using d (not R) as the offset along each
//   edge is what makes the arc tangent — using R directly (the old approach)
//   only works for 90° corners and produces visible kinks at other angles.
//
//   The radius is clamped per-vertex so adjacent fillets never overlap: the
//   tangent distance d must not exceed half of either incident edge. Since
//   d = R / tan(θ/2), this means  R ≤ (edge/2) · tan(θ/2)  for each edge.
//
//   points  - array of [x, y] pairs (the polygon vertices, in order)
//   radius  - desired corner radius (absolute px)
//
// Returns an SVG path "d" string. When radius is 0 the path is equivalent to
// the original polygon (straight lines, no arcs).
export function roundedPolygonPath(points, radius) {
  const n = points.length;
  if (n < 3) return "";
  const r = Math.max(0, Number(radius) || 0);
  if (r === 0) {
    // No rounding — emit a plain polygon path.
    return `M ${points[0][0]} ${points[0][1]} ` +
      points.slice(1).map((p) => `L ${p[0]} ${p[1]}`).join(" ") +
      " Z";
  }

  // Precompute edge vectors and per-vertex data.
  // edgeLen[i] = length of edge from vertex i to vertex (i+1).
  const edgeLen = [];
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    edgeLen.push(Math.hypot(b[0] - a[0], b[1] - a[1]) || 1e-9);
  }

  // For each vertex, compute the interior half-angle and the clamped radius.
  // The fillet at vertex i is bounded by the two incident edges:
  //   incoming edge = edge (i-1 -> i), length edgeLen[i-1]
  //   outgoing edge = edge (i -> i+1), length edgeLen[i]
  // Tangent distance d = R / tan(θ/2) must satisfy:
  //   d ≤ edgeLen[i-1]/2   and   d ≤ edgeLen[i]/2
  // => R ≤ (edgeLen[i-1]/2) · tan(θ/2)  and  R ≤ (edgeLen[i]/2) · tan(θ/2)
  const vertR = [];   // clamped radius per vertex
  const vertD = [];   // tangent distance per vertex (= R / tan(θ/2))
  const vertSweep = []; // sweep flag per vertex
  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    // Incoming edge direction (prev -> curr), unit vector.
    const inDx = curr[0] - prev[0];
    const inDy = curr[1] - prev[1];
    const inLen = edgeLen[(i - 1 + n) % n];
    const inUx = inDx / inLen;
    const inUy = inDy / inLen;

    // Outgoing edge direction (curr -> next), unit vector.
    const outDx = next[0] - curr[0];
    const outDy = next[1] - curr[1];
    const outLen = edgeLen[i];
    const outUx = outDx / outLen;
    const outUy = outDy / outLen;

    // Interior half-angle θ/2 via the dot product of the REVERSED incoming
    // edge (curr -> prev) and the outgoing edge (curr -> next). These two
    // vectors point away from the vertex along the two edges, so the angle
    // between them IS the interior angle θ.
    const revInUx = -inUx;
    const revInUy = -inUy;
    let cosTheta = revInUx * outUx + revInUy * outUy;
    cosTheta = Math.max(-1, Math.min(1, cosTheta));
    const theta = Math.acos(cosTheta); // interior angle, 0 < θ < π
    const halfTan = Math.tan(theta / 2);

    // Clamp R so the tangent distance fits within both incident edges.
    // d = R / tan(θ/2) ≤ min(inLen/2, outLen/2)
    // => R ≤ min(inLen/2, outLen/2) · tan(θ/2)
    // Guard against halfTan → 0 (degenerate, near-180° straight angle) and
    // halfTan → ∞ (near-0° spike, where tan(θ/2) blows up — clamp by edge).
    let Ri = r;
    if (halfTan > 1e-6) {
      const maxByIn = (inLen / 2) * halfTan;
      const maxByOut = (outLen / 2) * halfTan;
      Ri = Math.min(Ri, maxByIn, maxByOut);
    } else {
      // Nearly straight angle — tiny rounding, clamp to half the shorter edge.
      Ri = Math.min(Ri, inLen / 2, outLen / 2);
    }
    Ri = Math.max(0, Ri);

    // Tangent distance along each edge from the vertex.
    const Di = halfTan > 1e-6 ? Ri / halfTan : 0;

    // Sweep flag: cross product of incoming × outgoing edge vectors.
    // cross < 0 -> right turn (CW polygon in y-down) -> sweep 0
    // cross > 0 -> left turn  (CCW polygon in y-down) -> sweep 1
    // This automatically handles concave vertices (opposite turn direction).
    const cross = inDx * outDy - inDy * outDx;
    const sweep = cross < 0 ? 0 : 1;

    vertR.push(Ri);
    vertD.push(Di);
    vertSweep.push(sweep);
  }

  // Build the path. For each vertex i:
  //   - The incoming edge runs from vertex (i-1) to vertex i. The fillet at
  //     vertex i starts at distance vertD[i] back from i along that edge.
  //   - The fillet ends at distance vertD[i] forward from i along the
  //     outgoing edge.
  //   - Between two consecutive fillets, the straight edge segment connects
  //     fillet-end of vertex i to fillet-start of vertex (i+1).
  const parts = [];
  for (let i = 0; i < n; i++) {
    const curr = points[i];
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];

    const inLen = edgeLen[(i - 1 + n) % n];
    const outLen = edgeLen[i];
    const inUx = (curr[0] - prev[0]) / inLen;
    const inUy = (curr[1] - prev[1]) / inLen;
    const outUx = (next[0] - curr[0]) / outLen;
    const outUy = (next[1] - curr[1]) / outLen;

    const Di = vertD[i];
    const Ri = vertR[i];
    const sweep = vertSweep[i];

    // Fillet start: walk back from curr along the incoming edge by Di.
    const startX = curr[0] - inUx * Di;
    const startY = curr[1] - inUy * Di;
    // Fillet end: walk forward from curr along the outgoing edge by Di.
    const endX = curr[0] + outUx * Di;
    const endY = curr[1] + outUy * Di;

    if (i === 0) {
      parts.push(`M ${round(startX)} ${round(startY)}`);
    } else {
      parts.push(`L ${round(startX)} ${round(startY)}`);
    }
    // Tangent circular arc from start to end, radius = Ri.
    // large-arc-flag is 0: the arc never exceeds θ (always < π for a valid
    // polygon vertex), and Ri is clamped so it's a proper minor fillet.
    if (Ri > 0 && Di > 0) {
      parts.push(`A ${round(Ri)} ${round(Ri)} 0 0 ${sweep} ${round(endX)} ${round(endY)}`);
    } else {
      parts.push(`L ${round(endX)} ${round(endY)}`);
    }
  }
  parts.push("Z");
  return parts.join(" ");
}

// Parse an SVG polygon "points" string into an array of [x, y] pairs.
export function parsePoints(str) {
  const nums = (str || "").match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi);
  if (!nums) return [];
  const pts = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    pts.push([Number(nums[i]), Number(nums[i + 1])]);
  }
  return pts;
}

// Resolve a single corner-radius value (px or %) to absolute pixels, based on
// the element's bounding-box size. Exported so panels / renderers can share
// the same resolution logic for non-rect shapes (polygon, path, etc.).
export function resolveCornerRadius(attrs, w, h) {
  const unit = attrs.cornerRadiusUnit === "%" ? "%" : "px";
  return resolveRadius(attrs.cornerRadius, unit, w, h);
}

// Internal: round to 2 decimals to keep path strings compact.
function round(v) {
  return Math.round(v * 100) / 100;
}
